import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import {
  DynamoDBDocumentClient,
  GetCommand,
  PutCommand,
  QueryCommand,
  ScanCommand,
} from "@aws-sdk/lib-dynamodb";
import { credentialsProvider } from "./auth";
import { fetchAuthSession } from "aws-amplify/auth";
import { TypeExamAPI, TypeExamStatus } from "app/constants/types/exam.types";
import { TypeSortByExam } from "app/pages/ExamPage/utils/types";
import { AUTH_ROUTE } from "app/constants/route";

type School = any;
type User = any;

const region = process.env.REACT_APP_AWS_REGION;
const usersTable = process.env.REACT_APP_STUDENT_TABLE;
const schoolsTable = process.env.REACT_APP_SCHOOL_TABLE;
const examsTable = process.env.REACT_APP_EXAM_TABLE;

// Validate DynamoDB configuration
if (!region || !examsTable) {
  console.error("DynamoDB configuration is missing:", {
    region,
    usersTable: usersTable || "Not set",
    schoolsTable: schoolsTable || "Not set",
    examsTable: examsTable || "Not set",
  });
  throw new Error(
    "DynamoDB configuration is missing. Check environment variables."
  );
}

// Create DynamoDB client with error handling
let client: DynamoDBClient;
let docClient: DynamoDBDocumentClient;

/**
 * Initialize or reinitialize the DynamoDB client with authenticated credentials
 * This function now ensures the user is authenticated before initializing
 */
export const initializeDynamoDBClient =
  async (): Promise<DynamoDBDocumentClient> => {
    // console.log(
    //   "Initializing DynamoDB client with authenticated credentials..."
    // );

    try {
      // First check if the user is authenticated
      const session = await fetchAuthSession();

      // console.log("Dynamo session:", session);
      if (!session.tokens) {
        console.error("Authentication required to access DynamoDB");
        throw new Error("Authentication required to access DynamoDB");
      }

      client = new DynamoDBClient({
        region,
        credentials: async () => {
          // Get fresh credentials on each request
          const session = await fetchAuthSession();
          if (!session.tokens) {
            throw new Error("Authentication required to access DynamoDB");
          }
          return await credentialsProvider();
        },
      });

      // Create DynamoDB Document client
      docClient = DynamoDBDocumentClient.from(client);
      // console.log(
      //   "DynamoDB client created successfully with authenticated credentials"
      // );

      return docClient;
    } catch (error) {
      console.error("Failed to create DynamoDB client:", error);
      throw new Error(
        `Failed to initialize DynamoDB client: ${error instanceof Error ? error.message : "Authentication required"
        }`
      );
    }
  };

/**
 * Refresh the DynamoDB client to use the latest credentials
 * This should be called after authentication state changes
 */
export const refreshDynamoDBClient = async (): Promise<void> => {
  try {
    // Check if the user is authenticated
    const session = await fetchAuthSession();
    console.log(
      "Refreshing DynamoDB client. User authenticated:",
      !!session.tokens
    );

    if (!session.tokens) {
      console.log("User not authenticated, clearing DynamoDB client");
      // Clear the client references to force reinitialization on next use
      client = null as unknown as DynamoDBClient;
      docClient = null as unknown as DynamoDBDocumentClient;
      return;
    }

    // Reinitialize the client with authenticated credentials
    await initializeDynamoDBClient();
    console.log(
      "DynamoDB client refreshed successfully with authenticated credentials"
    );
  } catch (error) {
    console.error("Error refreshing DynamoDB client:", error);
    // Clear the client references to force reinitialization on next use
    client = null as unknown as DynamoDBClient;
    docClient = null as unknown as DynamoDBDocumentClient;
  }
};

// We no longer initialize the client automatically
// It will be initialized on-demand when needed, after checking authentication

// User operations
export const getUserByCognitoId = async (
  cognitoId: string
): Promise<User | null> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to access user data");
      throw new Error(
        "Authentication required to view user data. Please sign in."
      );
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }

    console.log(`Querying users table for cognitoId: ${cognitoId}`);

    if (!cognitoId) {
      console.error("Invalid cognitoId provided:", cognitoId);
      throw new Error("Invalid cognitoId: Cannot query with empty ID");
    }

    const response = await docClient.send(
      new QueryCommand({
        TableName: usersTable,
        IndexName: "GSI-CognitoId",
        KeyConditionExpression: "cognitoId = :cognitoId",
        ExpressionAttributeValues: {
          ":cognitoId": cognitoId,
        },
      })
    );

    // console.log(
    //   `Query successful, found ${response.Items?.length || 0} matching users`
    // );

    if (response.Items?.[0]) {
      return response.Items[0] as User;
    }

    return null;
  } catch (error) {
    console.error("Error querying users table:", error);

    // Provide more specific error messages based on error type
    if (error instanceof Error) {
      // Handle authentication errors
      if (
        error.message.includes("Authentication required") ||
        error.message.includes("Unauthenticated access is not supported")
      ) {
        console.error("Authentication required to access user data");

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view user data. Please sign in."
        );
      }

      if (error.name === "ResourceNotFoundException") {
        throw new Error(`DynamoDB table not found: ${usersTable}`);
      } else if (error.name === "AccessDeniedException") {
        throw new Error(`Access denied to DynamoDB table: ${usersTable}`);
      } else if (error.name === "ValidationException") {
        throw new Error(`DynamoDB validation error: ${error.message}`);
      } else if (error.message.includes("NetworkError")) {
        throw new Error("Network error when connecting to DynamoDB");
      } else {
        throw new Error(`DynamoDB error: ${error.message}`);
      }
    }

    throw new Error("Unknown error occurred when fetching user data");
  }
};

export const getUserByEmail = async (email: string): Promise<User | null> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to access user data");
      throw new Error(
        "Authentication required to view user data. Please sign in."
      );
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }

    console.log(`Querying users table for email: ${email}`);

    if (!email) {
      console.error("Invalid email provided:", email);
      throw new Error("Invalid email: Cannot query with empty email");
    }

    const response = await docClient.send(
      new QueryCommand({
        TableName: usersTable,
        IndexName: "GSI-Email",
        KeyConditionExpression: "email = :email",
        ExpressionAttributeValues: {
          ":email": email,
        },
      })
    );

    // console.log(
    //   `Query successful, found ${response.Items?.length || 0} matching users`
    // );

    if (response.Items?.[0]) {
      return response.Items[0] as User;
    }

    return null;
  } catch (error) {
    console.error("Error querying users table by email:", error);

    // Provide more specific error messages based on error type
    if (error instanceof Error) {
      // Handle authentication errors
      if (
        error.message.includes("Authentication required") ||
        error.message.includes("Unauthenticated access is not supported")
      ) {
        console.error("Authentication required to access user data");

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view user data. Please sign in."
        );
      }

      if (error.name === "ResourceNotFoundException") {
        throw new Error(`DynamoDB table not found: ${usersTable}`);
      } else if (error.name === "AccessDeniedException") {
        throw new Error(`Access denied to DynamoDB table: ${usersTable}`);
      } else if (error.name === "ValidationException") {
        throw new Error(`DynamoDB validation error: ${error.message}`);
      } else if (error.message.includes("NetworkError")) {
        throw new Error("Network error when connecting to DynamoDB");
      } else {
        throw new Error(`DynamoDB error: ${error.message}`);
      }
    }

    throw new Error("Unknown error occurred when fetching user data by email");
  }
};

export const createOrUpdateUser = async (
  user: Partial<User> & { cognitoId: string }
): Promise<User> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to create/update user data");
      throw new Error(
        "Authentication required to create/update user data. Please sign in."
      );
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }

    console.log(`Creating/updating user with cognitoId: ${user.cognitoId}`);

    if (!user.cognitoId) {
      console.error("Invalid user data:", user);
      throw new Error("Invalid user data: cognitoId is required");
    }

    const timestamp = Math.floor(Date.now() / 1000);
    const userId = user.userId || crypto.randomUUID();

    const item: User = {
      userId,
      cognitoId: user.cognitoId,
      displayName: user.displayName || "",
      email: user.email || "",
      role: user.role || "student",
      createdAt: user.createdAt || timestamp,
      updatedAt: timestamp,
    };

    console.log("Saving user data:", {
      ...item,
      userId: item.userId,
      cognitoId: item.cognitoId,
    });

    await docClient.send(
      new PutCommand({
        TableName: usersTable,
        Item: item,
      })
    );

    console.log("User data saved successfully");
    return item;
  } catch (error) {
    console.error("Error creating/updating user:", error);

    // Provide more specific error messages based on error type
    if (error instanceof Error) {
      // Handle authentication errors
      if (
        error.message.includes("Authentication required") ||
        error.message.includes("Unauthenticated access is not supported")
      ) {
        console.error("Authentication required to create/update user data");

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to create/update user data. Please sign in."
        );
      }

      if (error.name === "ResourceNotFoundException") {
        throw new Error(`DynamoDB table not found: ${usersTable}`);
      } else if (error.name === "AccessDeniedException") {
        throw new Error(`Access denied to DynamoDB table: ${usersTable}`);
      } else if (error.name === "ValidationException") {
        throw new Error(`DynamoDB validation error: ${error.message}`);
      } else if (error.message.includes("NetworkError")) {
        throw new Error("Network error when connecting to DynamoDB");
      } else {
        throw new Error(`DynamoDB error: ${error.message}`);
      }
    }

    throw new Error("Unknown error occurred when saving user data");
  }
};

// School operations
export const listSchools = async (): Promise<School[]> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to access schools");
      throw new Error(
        "Authentication required to view schools. Please sign in."
      );
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }

    console.log(`Scanning DynamoDB table for schools: ${schoolsTable}`);
    console.log("Environment variables check:", {
      region,
      schoolsTable,
      fullEnv: Object.keys(process.env)
        .filter((key) => key.includes("SCHOOL"))
        .reduce((obj, key) => {
          obj[key] = process.env[key];
          return obj;
        }, {} as Record<string, string | undefined>),
    });

    const response = await docClient.send(
      new ScanCommand({
        TableName: schoolsTable,
      })
    );

    console.log(
      // `Scan successful, found ${response.Items?.length || 0} schools:`,
      response.Items
    );
    return (response.Items || []) as School[];
  } catch (error) {
    console.error("Error scanning schools table:", error);

    // Handle specific errors
    if (error instanceof Error) {
      // Handle authentication errors specifically
      if (error.message.includes("Authentication required")) {
        console.error(
          "Authentication required to access schools. Redirecting to sign-in page."
        );

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view schools. Redirecting to sign-in page."
        );
      }

      // Handle the specific error from Cognito when unauthenticated access is disabled
      if (error.message.includes("Unauthenticated access is not supported")) {
        console.error(
          "Authentication required for access. Redirecting to sign-in page."
        );

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view schools. Redirecting to sign-in page."
        );
      }

      if (error.name === "ResourceNotFoundException") {
        console.warn(
          `DynamoDB table not found: ${schoolsTable}. Returning empty array.`
        );
        return [];
      } else if (error.name === "AccessDeniedException") {
        console.warn(
          `Access denied to DynamoDB table: ${schoolsTable}. Returning empty array.`
        );
        return [];
      } else if (error.name === "ProvisionedThroughputExceededException") {
        console.warn(
          `DynamoDB throughput exceeded for table: ${schoolsTable}. Returning empty array.`
        );
        return [];
      } else if (error.message.includes("NetworkError")) {
        console.warn(
          "Network error when connecting to DynamoDB. Returning empty array."
        );
        return [];
      } else {
        // For other errors, still throw to show the error UI
        throw new Error(`DynamoDB error: ${error.message}`);
      }
    }

    // For unknown errors, return empty array to show empty state
    console.warn(
      "Unknown error occurred when fetching schools. Returning empty array."
    );
    return [];
  }
};

export const getSchool = async (id: string): Promise<School | null> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to access school data");
      throw new Error(
        "Authentication required to view school data. Please sign in."
      );
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }

    const response = await docClient.send(
      new GetCommand({
        TableName: schoolsTable,
        Key: { schoolId: id },
      })
    );

    if (response.Item) {
      return response.Item as School;
    }

    return null;
  } catch (error) {
    console.error("Error getting school:", error);

    // Handle specific errors
    if (error instanceof Error) {
      // Handle authentication errors
      if (
        error.message.includes("Authentication required") ||
        error.message.includes("Unauthenticated access is not supported")
      ) {
        console.error("Authentication required to access school data");

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view school data. Please sign in."
        );
      }

      if (error.name === "ValidationException") {
        console.error(
          `DynamoDB schema validation error. Key used: schoolId: ${id}, type: ${typeof id}`
        );
      }
    }
    throw error;
  }
};

// Exam operations
export const getExam = async (id: string): Promise<TypeExamAPI[] | null> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to access exam data");
      throw new Error(
        "Authentication required to view exam data. Please sign in."
      );
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }

    const response = await docClient.send(
      new GetCommand({
        TableName: examsTable,
        Key: { examId: id },
      })
    );

    if (response.Item) {
      return response.Item as TypeExamAPI[];
    }

    return null;
  } catch (error) {
    console.error("Error getting exam:", error);

    // Handle specific errors
    if (error instanceof Error) {
      // Handle authentication errors
      if (
        error.message.includes("Authentication required") ||
        error.message.includes("Unauthenticated access is not supported")
      ) {
        console.error("Authentication required to access exam data");

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view exam data. Please sign in."
        );
      }

      if (error.name === "ValidationException") {
        console.error(
          `DynamoDB schema validation error. Key used: examId: ${id}, type: ${typeof id}`
        );
      }
    }
    throw error;
  }
};

/**
 * Get a specific exam by its ID
 * @param examId - The ID of the exam to retrieve
 * @returns The exam data or null if not found
 */
export const getExamById = async (examId: string): Promise<TypeExamAPI | null> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to access exam data");
      throw new Error(
        "Authentication required to view exam data. Please sign in."
      );
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }

    // console.log(`Querying exams table for examId: ${examId}`);

    if (!examId) {
      console.error("Invalid examId provided:", examId);
      throw new Error("Invalid examId: Cannot query with empty ID");
    }

    const response = await docClient.send(
      new QueryCommand({
        TableName: examsTable,
        KeyConditionExpression: "examId = :examId",
        ExpressionAttributeValues: {
          ":examId": examId,
        },
      })
    );

    // console.log(
    //   `Query successful, found ${response.Items?.length || 0} matching exams`
    // );

    if (response.Items?.[0]) {
      return response.Items[0] as TypeExamAPI;
    }

    return null;
  } catch (error) {
    console.error("Error querying exams table:", error);

    // Provide more specific error messages based on error type
    if (error instanceof Error) {
      // Handle authentication errors
      if (
        error.message.includes("Authentication required") ||
        error.message.includes("Unauthenticated access is not supported")
      ) {
        console.error("Authentication required to access exam data");

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view exam data. Please sign in."
        );
      }

      if (error.name === "ResourceNotFoundException") {
        throw new Error(`DynamoDB table not found: ${examsTable}`);
      } else if (error.name === "AccessDeniedException") {
        throw new Error(`Access denied to DynamoDB table: ${examsTable}`);
      } else if (error.name === "ValidationException") {
        throw new Error(`DynamoDB validation error: ${error.message}`);
      } else if (error.message.includes("NetworkError")) {
        throw new Error("Network error when connecting to DynamoDB");
      } else {
        throw new Error(`DynamoDB error: ${error.message}`);
      }
    }

    throw new Error("Unknown error occurred when fetching exam data");
  }
};

export const listExams = async (params?: { status?: TypeExamStatus, searchQuery?: string, sortBy?: { field: TypeSortByExam, order: 'asc' | 'desc' } }): Promise<TypeExamAPI[]> => {
  try {
    // Check authentication first
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error("Authentication required to access exams");
      throw new Error("Authentication required to view exams. Please sign in.");
    }

    // Initialize client if not already done
    if (!docClient) {
      await initializeDynamoDBClient();
    }


    // Build the query based on provided parameters
    let queryCommandInput: any = {
      TableName: examsTable,
    };
   
    
    // If no parameters are provided, use ScanCommand to show all exams
    const command = Object.keys(queryCommandInput).length > 1 ? new QueryCommand(queryCommandInput) : new ScanCommand({ TableName: examsTable });

    const response = await docClient.send(command);

    return (response.Items || []) as TypeExamAPI[];
  } catch (error) {
    console.error("Error querying exams table:", error);

    // Handle specific errors
    if (error instanceof Error) {
      // Handle authentication errors specifically
      if (error.message.includes("Authentication required")) {
        console.error(
          "Authentication required to access exams. Redirecting to sign-in page."
        );

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view exams. Redirecting to sign-in page."
        );
      }

      // Handle the specific error from Cognito when unauthenticated access is disabled
      if (error.message.includes("Unauthenticated access is not supported")) {
        console.error(
          "Authentication required for access. Redirecting to sign-in page."
        );

        // If we're in a browser environment, redirect to sign-in
        if (typeof window !== "undefined") {
          window.location.href = AUTH_ROUTE;
        }

        throw new Error(
          "Authentication required to view exams. Redirecting to sign-in page."
        );
      }

      if (error.name === "ResourceNotFoundException") {
        console.warn(
          `DynamoDB table not found: ${examsTable}. Returning empty array.`
        );
        return [];
      } else if (error.name === "AccessDeniedException") {
        console.warn(
          `Access denied to DynamoDB table: ${examsTable}. Returning empty array.`
        );
        return [];
      } else if (error.name === "ProvisionedThroughputExceededException") {
        console.warn(
          `DynamoDB throughput exceeded for table: ${examsTable}. Returning empty array.`
        );
        return [];
      } else if (error.message.includes("NetworkError")) {
        console.warn(
          "Network error when connecting to DynamoDB. Returning empty array."
        );
        return [];
      } else {
        // For other errors, still throw to show the error UI
        throw new Error(`DynamoDB error: ${error.message}`);
      }
    }

    // For unknown errors, return empty array to show empty state
    console.warn(
      "Unknown error occurred when fetching exams. Returning empty array."
    );
    return [];
  }
};
